/* eslint-disable no-relative-import-paths/no-relative-import-paths */
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { authTokenInterceptor } from '@interceptors/auth-token.interceptor';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { NgxPermissionsModule, NgxPermissionsService, NgxRolesService } from 'ngx-permissions';

import { routes } from './app.routes';
import { SwUpdateService } from './core/services/helper/sw-update.service';

/** TanStack Query client — single instance for the entire app */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24h — required for persisted queries to be restorable
    },
  },
});

/* Expose query client globally for devtools */
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import('@tanstack/query-core').QueryClient;
  }
}
window.__TANSTACK_QUERY_CLIENT__ = queryClient;

/* Query keys that should NEVER be persisted to localStorage.
 * All server-paginated list queries live here so reload shows a fresh
 * shimmer instead of stale cached data. In-session navigation still
 * benefits from in-memory cache (instant page-flip, silent background refetch).
 *
 * `authenticated-user-options` is per-USER data (`/me/options` — saleboard zoom,
 * dashboard order). The persisted cache is keyed only by query key, not by user,
 * and nothing clears it on logout — so a second user signing in on the same
 * browser restored the first user's preferences and appeared to have them
 * "changed for everyone". It is a tiny request; always fetch it fresh.
 *
 * `authenticated-user` (`/me`) carries `role.permissions`, which
 * `UserPermissionService.setPermissionsFromUser` turns into the whole app's
 * permission set. Persisting it has the same cross-user flaw as the options
 * above, and a worse consequence: after an admin edits a role on Settings ›
 * Permissions, the affected user's next load replays the dehydrated `/me` and
 * re-applies the OLD permission set — for up to `maxAge` — which reads exactly
 * as "the permissions are not working" (FEC-1160 QA, 18 + 21 Aug). Authorisation
 * input must never come from a stale cache; it is one small request. */
const PERSIST_QUERY_BLOCKLIST: readonly string[] = [
  'authenticated-user-options',
  'authenticated-user',
];

/* Persist the query cache to localStorage via TanStack's official persister.
 * Throttled writes + maxAge + auto quota-error handling are built in. */
if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
  const persister = createSyncStoragePersister({
    storage: localStorage,
    key: 'angular-query-cache',
    throttleTime: 2000,
  });

  persistQueryClient({
    queryClient,
    persister,
    maxAge: 1000 * 60 * 60 * 9, // 9h
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        if (query.state.status !== 'success') return false;
        const firstKey = query.queryKey?.[0];
        return !(typeof firstKey === 'string' && PERSIST_QUERY_BLOCKLIST.includes(firstKey));
      },
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideAnimationsAsync(),

    /* CDK overlays (dialogs, popups) must stay in the normal stacking context.
     *
     * From v21 the CDK renders every overlay through the native Popover API,
     * which puts it in the browser TOP LAYER — a layer that sits above the whole
     * document and ignores z-index entirely. Anything NOT in the top layer then
     * paints behind an open dialog no matter how high its z-index, which broke
     * every `appendTo="body"` ng-select inside a dialog: the panel is in the DOM
     * and reports itself visible, but is painted underneath the dialog, so the
     * dropdown reads as empty and its options cannot be clicked.
     *
     * Opting out restores the z-index:1000 overlay container the app's CSS is
     * written against (`.ng-dropdown-panel` is 1050, so panels win again). */
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },

    /* Chart.js is NOT provided globally — it is registered at the lazy dashboard
     * route (see dashboard.routes.ts) so chart.js stays out of the initial bundle. */

    /* HTTP client with fetch API and auth interceptor */
    provideHttpClient(withFetch(), withInterceptors([authTokenInterceptor])),

    /* TanStack Query */
    provideTanStackQuery(queryClient),

    /* ngx-permissions */
    NgxPermissionsService,
    NgxRolesService,
    importProvidersFrom(NgxPermissionsModule.forRoot()),

    /* Router with scroll restoration */
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),

    /* SSR hydration */
    provideClientHydration(withEventReplay()),

    /* JWT helper (no options needed — token is managed manually) */
    JwtHelperService,
    {
      provide: JWT_OPTIONS,
      useValue: {},
    },

    /* Service Worker update checker */
    {
      provide: APP_INITIALIZER,
      useFactory: (swUpdateService: SwUpdateService) => () => swUpdateService.checkForUpdates(),
      deps: [SwUpdateService],
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
